const DB_NAME = "wyni-app-storage";
const STORE_NAME = "keyval";
const DB_VERSION = 1;

export const isIndexedDbAvailable = (): boolean =>
  typeof indexedDB !== "undefined";

const openDb = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    if (!isIndexedDbAvailable()) {
      reject(new Error("IndexedDB not available"));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("Failed to open IndexedDB"));
  });
};

const withStore = async <T>(
  mode: IDBTransactionMode,
  handler: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> => {
  const db = await openDb();

  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode);
    const store = tx.objectStore(STORE_NAME);
    const request = handler(store);
    let requestDone = false;
    let txDone = false;
    let result: T | undefined;
    let settled = false;

    const resolveIfReady = () => {
      if (settled || !requestDone || !txDone) return;
      settled = true;
      resolve(result as T);
    };

    const rejectOnce = (error: Error) => {
      if (settled) return;
      settled = true;
      reject(error);
    };

    request.onsuccess = () => {
      result = request.result as T;
      requestDone = true;
      resolveIfReady();
    };
    request.onerror = () => {
      rejectOnce(request.error ?? new Error("IndexedDB request failed"));
    };

    tx.oncomplete = () => {
      db.close();
      txDone = true;
      resolveIfReady();
    };
    tx.onerror = () => {
      db.close();
      rejectOnce(tx.error ?? new Error("IndexedDB transaction failed"));
    };
    tx.onabort = () => {
      db.close();
      rejectOnce(tx.error ?? new Error("IndexedDB transaction aborted"));
    };
  });
};

export const getItem = async (key: string): Promise<string | null> => {
  if (!isIndexedDbAvailable()) {
    return null;
  }

  try {
    const value = await withStore<unknown>("readonly", (store) => store.get(key));
    if (typeof value === "string") {
      return value;
    }
    if (value === undefined || value === null) {
      return null;
    }
    return JSON.stringify(value);
  } catch {
    return null;
  }
};

export const setItem = async (key: string, value: string): Promise<void> => {
  if (!isIndexedDbAvailable()) {
    return;
  }

  await withStore("readwrite", (store) => store.put(value, key));
};

export const removeItem = async (key: string): Promise<void> => {
  if (!isIndexedDbAvailable()) {
    return;
  }

  await withStore("readwrite", (store) => store.delete(key));
};
